// ─── src/firebase.js ───
// 가득 앱 Firebase 연동 설정 (네이티브 Google 로그인 지원)

import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence,
         GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithCredential,
         signInAnonymously, createUserWithEmailAndPassword,
         signInWithEmailAndPassword, signOut, onAuthStateChanged, deleteUser } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, deleteDoc,
         collection, getDocs, addDoc, query, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// ════════════════════════════════════════════
// Firebase Config
// ════════════════════════════════════════════
const firebaseConfig = {
  apiKey: "AIzaSyBT77aQgvbNlCN420phTVL3KveAEYpojJw",
  authDomain: "gadeuk-app.firebaseapp.com",
  projectId: "gadeuk-app",
  storageBucket: "gadeuk-app.firebasestorage.app",
  messagingSenderId: "838429908478",
  appId: "1:838429908478:web:65af105f73dbe85a0a34af",
  measurementId: "G-KH1LFXQKNT"
};

const app = initializeApp(firebaseConfig);

// iOS WKWebView(capacitor://)에서 getAuth()는 persistence 자동감지 단계에 멈춰
// onAuthStateChanged가 발화하지 않음 → 네이티브에선 persistence를 명시해 초기화.
const _isNativePlatform = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
export const auth = _isNativePlatform
  ? initializeAuth(app, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] })
  : getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

// 플랫폼 감지
const isNativePlatform = () => {
  return window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
};

// ═══════════════════════════════════════════
// 인증 함수
// ═══════════════════════════════════════════

// Google 로그인 (네이티브: Capacitor SDK / 웹: signInWithPopup)
export const loginWithGoogle = async () => {
  try {
    if (isNativePlatform()) {
      // 네이티브 앱 (Android)
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();
      const credential = GoogleAuthProvider.credential(result.credential?.idToken);
      const userCredential = await signInWithCredential(auth, credential);
      return { success: true, user: userCredential.user };
    } else {
      // 웹 브라우저
      const result = await signInWithPopup(auth, googleProvider);
      return { success: true, user: result.user };
    }
  } catch (error) {
    console.error('Google login error:', error);
    let msg = '로그인에 실패했습니다. 다시 시도해주세요.';
    if (error.code === 'auth/popup-closed-by-user' || error.message?.includes('cancel')) {
      msg = '로그인이 취소되었습니다.';
    }
    return { success: false, error: msg };
  }
};

// Apple 로그인 (네이티브: Capacitor SDK / 웹: signInWithPopup)
// App Store 지침 4.8 — 제3자 로그인(구글) 제공 시 iOS에선 Apple 로그인도 필수.
export const loginWithApple = async () => {
  try {
    if (isNativePlatform()) {
      // 네이티브 앱 (iOS) — 네이티브 Apple 시트 → idToken+nonce로 Firebase 자격증명 생성
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: result.credential?.idToken,
        rawNonce: result.credential?.nonce,
      });
      const userCredential = await signInWithCredential(auth, credential);
      return { success: true, user: userCredential.user };
    } else {
      // 웹 브라우저
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      return { success: true, user: result.user };
    }
  } catch (error) {
    console.error('Apple login error:', error);
    let msg = '로그인에 실패했습니다. 다시 시도해주세요.';
    const m = (error.message || '') + '';
    if (error.code === 'auth/popup-closed-by-user' || error.code === '1001' ||
        m.includes('cancel') || m.includes('1001') || m.includes('AuthorizationError')) {
      msg = '로그인이 취소되었습니다.';
    }
    return { success: false, error: msg };
  }
};

// 호환성 유지
export const checkRedirectResult = async () => {
  return { success: false, error: null };
};

// 이메일/비밀번호 회원가입
export const registerWithEmail = async (email, password) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return { success: true, user: result.user };
  } catch (error) {
    let msg = error.message;
    if (error.code === 'auth/email-already-in-use') msg = '이미 가입된 이메일입니다.';
    if (error.code === 'auth/weak-password') msg = '비밀번호는 6자 이상이어야 합니다.';
    if (error.code === 'auth/invalid-email') msg = '올바른 이메일 형식이 아닙니다.';
    return { success: false, error: msg };
  }
};

// 이메일/비밀번호 로그인
export const loginWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: result.user };
  } catch (error) {
    let msg = error.message;
    if (error.code === 'auth/user-not-found') msg = '가입되지 않은 이메일입니다.';
    if (error.code === 'auth/wrong-password') msg = '비밀번호가 틀렸습니다.';
    if (error.code === 'auth/invalid-email') msg = '올바른 이메일 형식이 아닙니다.';
    return { success: false, error: msg };
  }
};

// 비회원(익명) 로그인
export const loginAnonymously = async () => {
  try {
    const result = await signInAnonymously(auth);
    return { success: true, user: result.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 로그아웃
export const logout = async () => {
  try {
    if (isNativePlatform()) {
      try {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signOut();
      } catch(e) {}
    }
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 로그인 상태 감시
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// 회원 탈퇴 — 계정 + 모든 개인 데이터 삭제 (App Store 지침 5.1.1(v) 필수)
// 순서: Firestore 개인데이터 삭제(인증 상태에서) → 인증 계정 삭제.
// 최근 로그인 만료 시 auth/requires-recent-login → code:'reauth' 반환(UI가 재로그인 후 재시도).
export const deleteAccount = async () => {
  const user = auth.currentUser;
  if (!user) return { success: false, error: '로그인이 필요해요.' };
  const uid = user.uid;
  try {
    // 1) Firestore 개인 데이터 삭제 — 신청상태·설정(즐겨찾기) 서브컬렉션
    try { const apps = await getDocs(collection(db, 'users', uid, 'applications')); await Promise.all(apps.docs.map((d) => deleteDoc(d.ref))); } catch (e) {}
    try { const sets = await getDocs(collection(db, 'users', uid, 'settings')); await Promise.all(sets.docs.map((d) => deleteDoc(d.ref))); } catch (e) {}
    // 2) 내가 올린 제보 + 첨부 사진(Storage) 삭제
    try {
      const rep = await getDocs(query(collection(db, 'userReports'), where('userId', '==', uid)));
      await Promise.all(rep.docs.map(async (d) => {
        const data = d.data();
        const photos = data.photos || (data.photoPath ? [{ path: data.photoPath }] : []);
        await Promise.all(photos.map((p) => (p && p.path ? deleteObject(storageRef(storage, p.path)).catch(() => {}) : null)));
        await deleteDoc(d.ref);
      }));
    } catch (e) {}
    // 3) 프로필 본체 삭제
    try { await deleteDoc(doc(db, 'users', uid)); } catch (e) {}
    // 4) 인증 계정 삭제
    await deleteUser(user);
    // 5) 네이티브 세션 정리
    if (isNativePlatform()) {
      try { const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication'); await FirebaseAuthentication.signOut(); } catch (e) {}
    }
    return { success: true };
  } catch (error) {
    if (error.code === 'auth/requires-recent-login') {
      return { success: false, code: 'reauth', error: '보안을 위해, 다시 로그인하신 뒤 탈퇴를 진행해 주세요.' };
    }
    return { success: false, error: error.message || '탈퇴 처리에 실패했어요.' };
  }
};

// ═══════════════════════════════════════════
// Firestore 함수
// ═══════════════════════════════════════════

export const saveUserProfile = async (userId, profile) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      ...profile,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserProfile = async (userId) => {
  try {
    const docSnap = await getDoc(doc(db, 'users', userId));
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    }
    return { success: true, data: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveApplicationStatus = async (userId, benefitId, status) => {
  try {
    await setDoc(doc(db, 'users', userId, 'applications', String(benefitId)), {
      benefitId: Number(benefitId),
      status,
      updatedAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getApplicationStatuses = async (userId) => {
  try {
    const querySnapshot = await getDocs(
      collection(db, 'users', userId, 'applications')
    );
    const statuses = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      statuses[data.benefitId] = data.status;
    });
    return { success: true, data: statuses };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveFavorites = async (userId, favorites) => {
  try {
    await setDoc(doc(db, 'users', userId, 'settings', 'favorites'), {
      ids: favorites,
      updatedAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getFavorites = async (userId) => {
  try {
    const docSnap = await getDoc(
      doc(db, 'users', userId, 'settings', 'favorites')
    );
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data().ids || {} };
    }
    return { success: true, data: {} };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ═══════════════════════════════════════════
// 사용자 제보 (5차 — 동네 소식 UGC)
// ═══════════════════════════════════════════

// 일일 제보 한도 체크 (localStorage 기반 1차 어뷰징 방지)
const DAILY_LIMIT = 5;
const getTodayKey = () => `gadeuk_reports_${new Date().toISOString().slice(0,10)}`;
export const getTodayReportCount = () => {
  try { return parseInt(localStorage.getItem(getTodayKey()) || '0', 10); }
  catch { return 0; }
};
const incrementTodayReportCount = () => {
  try {
    const k = getTodayKey();
    const c = getTodayReportCount() + 1;
    localStorage.setItem(k, String(c));
  } catch {}
};

// 사진 + 메타데이터 제보 (Storage 업로드 → Firestore 저장)
export const submitUserReport = async (userId, photoFiles, formData, profile) => {
  try {
    if (!userId) return { success: false, error: '로그인이 필요해요.' };
    // 2026-05-26 — photoFiles 배열 지원 (최대 3장). 하위호환: 단일 File 받으면 배열로 감쌈
    const files = Array.isArray(photoFiles) ? photoFiles : (photoFiles ? [photoFiles] : []);
    if (files.length === 0) return { success: false, error: '사진을 첨부해주세요.' };
    if (files.length > 3) return { success: false, error: '사진은 최대 3장까지 올릴 수 있어요.' };
    if (getTodayReportCount() >= DAILY_LIMIT) {
      return { success: false, error: `오늘 제보 한도(${DAILY_LIMIT}건)를 초과했어요. 내일 다시 시도해주세요.` };
    }

    // Storage 업로드 — 모든 사진 순차 업로드
    const ts = Date.now();
    const photos = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const ext = (f.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `userReports/${userId}/${ts}_${i}.${ext}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, f);
      const url = await getDownloadURL(sref);
      photos.push({ url, path });
    }

    // Firestore 저장
    const docRef = await addDoc(collection(db, 'userReports'), {
      userId,
      // 신규: photos 배열 (다중 사진) + 하위호환 photoUrl/photoPath
      photos,
      photoUrl: photos[0].url,
      photoPath: photos[0].path,
      title: (formData.title || '').slice(0, 80),
      place: (formData.place || '').slice(0, 60),
      comment: (formData.comment || '').slice(0, 200),
      city: profile.city || '',
      district: profile.district || '',
      dong: profile.dong || '',
      status: 'approved',
      flagCount: 0,
      likeCount: 0,
      createdAt: serverTimestamp(),
    });

    incrementTodayReportCount();
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('submitUserReport error:', error);
    return { success: false, error: error.message || '제보 저장에 실패했어요.' };
  }
};

// 우리 동네 제보 조회 (city + district 매칭, 최신 50건)
export const getUserReports = async (city, district) => {
  try {
    if (!city) return { success: true, data: [] };
    const q = query(
      collection(db, 'userReports'),
      where('city', '==', city),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
    const snap = await getDocs(q);
    const reports = [];
    snap.forEach(d => {
      const data = d.data();
      reports.push({
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || '',
      });
    });
    // 같은 시군구 가산점 (위로)
    if (district) {
      reports.sort((a, b) => {
        const aSame = a.district === district ? 0 : 1;
        const bSame = b.district === district ? 0 : 1;
        if (aSame !== bSame) return aSame - bSame;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
    }
    return { success: true, data: reports };
  } catch (error) {
    console.error('getUserReports error:', error);
    return { success: false, error: error.message };
  }
};

// 신고 (V1 — flagCount 증가만, 자동 비공개 처리는 V2)
export const flagUserReport = async (reportId) => {
  try {
    if (!reportId) return { success: false };
    const ref = doc(db, 'userReports', reportId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { success: false };
    const cur = snap.data().flagCount || 0;
    await setDoc(ref, { flagCount: cur + 1 }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 앱 버전 설정 조회 (인앱 업데이트 팝업용)
// 사용자 의견·피드백 저장 (2026-06-10) — MY > 의견 보내기
// category: 'bug'|'improve'|'praise'|'etc', rating: 1~5(선택), message: 자유 입력
export const submitFeedback = async (userId, { category, rating, message, appVersion, profile }) => {
  try {
    const docRef = await addDoc(collection(db, 'feedback'), {
      userId: userId || 'anonymous',
      category: category || 'etc',
      rating: rating || 0,
      message: (message || '').slice(0, 1000),
      appVersion: appVersion || '',
      city: profile?.city || '',
      district: profile?.district || '',
      status: 'new',
      createdAt: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('submitFeedback error:', error);
    return { success: false, error: error.message };
  }
};

// 프리미엄 수요조사 (2026-07-16, codex 수익화 1단계) — 익명 집계 전용.
// 원칙: uid·프로필·소득신호와 절대 결합하지 않음 (설계: premium-survey-design-2026-07-16.md)
export const submitPremiumSurvey = async ({ choices, willingness, appVersion }) => {
  try {
    await addDoc(collection(db, 'premiumSurvey'), {
      choices: (choices || []).slice(0, 5),
      willingness: willingness || '',
      appVersion: appVersion || '',
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('submitPremiumSurvey error:', error);
    return { success: false, error: error.message };
  }
};

// app_config/version 문서 포맷:
// { latestVersionCode: 18, latestVersionName: "2.3.1", forceUpdate: false, updateMessage: "..." }
export const getAppVersionConfig = async () => {
  try {
    const docSnap = await getDoc(doc(db, 'app_config', 'version'));
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    }
    return { success: true, data: null };
  } catch (error) {
    console.error('getAppVersionConfig error:', error);
    return { success: false, error: error.message };
  }
};
